import { useEffect, useState } from 'react';
import { useLastAppliedJobsHistory } from '../../../../../data/seeker';
import { useJobDetailsNewCollection } from '../../../../../data/jobDetailsNewCollection';
import { useFindJobState } from '../../../../../data/status';
import AppliedJobsRow from './AppliedJobsRow';

interface AppliedJobsHistoryProps {
    userId: string;
}

const AppliedJobsHistory = ({ userId }: AppliedJobsHistoryProps) => {
    const { data, isLoading } = useLastAppliedJobsHistory(userId);
    const [last60DaysRecords, setLast60DaysRecords] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);

    const recordsPerPage = 15;

    useEffect(() => {
        const nowSeconds = Math.floor(Date.now() / 1000);
        const sixtyDaysInSeconds = 60 * 24 * 60 * 60;
        const filteredData =
            data?.filter((item: any) => (nowSeconds - item.createdAt.seconds) <= sixtyDaysInSeconds) || [];
        setLast60DaysRecords(filteredData);
        setCurrentPage(1);
    }, [data]);

    const totalPages = Math.ceil(last60DaysRecords.length / recordsPerPage);
    const startIndex = (currentPage - 1) * recordsPerPage;
    const currentRecords = last60DaysRecords.slice(startIndex, startIndex + recordsPerPage);

    const getPageNumbers = () => {
        const pages: (number | string)[] = [];
        if (totalPages <= 5) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            if (currentPage <= 3) {
                pages.push(1, 2, 3, 4, 5, '...', totalPages);
            } else if (currentPage >= totalPages - 2) {
                pages.push(1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
            } else {
                pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
            }
        }
        return pages;
    };

    if (isLoading) {
        return <div>Loading...</div>;
    }

    return (
        <div className="w-full  by-white shadow-lg pb-8 pr-4">
            <table className="w-full ">
                <thead className="bg-white ">
                    <tr>
                        <th className="text-left py-5 px-2 col-span-2">Job Title</th>
                        <th className="text-left py-5 px-2">Job Current Status</th>
                        <th className="text-left py-5 px-2">Apply Date</th>
                    </tr>
                </thead>
                <tbody>
                    {currentRecords.map((job: any, index: number) => (
                        <AppliedJobsRow key={index} jobId={job?.jobId} appliedData={job.createdAt} />
                    ))}
                </tbody>
            </table>

            {totalPages > 1 && (
                <div className="flex justify-end mt-4 space-x-2">
                    <button
                        onClick={() => setCurrentPage((prev: number) => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className={`${currentPage !== 1 && 'bg-instant-teams-blue-Main text-white'} px-3 py-1 border rounded disabled:opacity-50`}
                    >
                        Prev
                    </button>

                    {getPageNumbers().map((page, idx) =>
                        typeof page === 'number' ? (
                            <button
                                key={idx}
                                onClick={() => setCurrentPage(page)}
                                className={`px-3 py-1 border rounded ${currentPage === page ? 'bg-instant-teams-blue-Main text-white' : ''}`}
                            >
                                {page}
                            </button>
                        ) : (
                            <span key={idx} className="px-2 py-1">...</span>
                        )
                    )}

                    <button
                        onClick={() => setCurrentPage((prev: number) => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className={`px-3 py-1 ${currentPage !== totalPages && 'bg-instant-teams-blue-Main text-white'} border rounded disabled:opacity-50`}
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
};

export default AppliedJobsHistory;
